import React, { useEffect, useState, useMemo } from "react";
import { useVerbatim } from "../context/VerbatimContext";
import { useSetup } from "../context/SetupContext";
import BaseModal from './BaseModal'; 
import LabelChip from "../components/LabelChip";
import { base64ToImage, renderVerbatimTranslation } from '../utils/VerbatimUtils';
import "./MergeLabelsModal.css"; 

const MergeLabelsModal = ({ isOpen, onClose }) => {
    const { verbatims, setVerbatims } = useVerbatim();
    const { labels, labelTypes, setLabels } = useSetup(); 
    const [selectedLabelType, setSelectedLabelType] = useState("");
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [newLabelName, setNewLabelName] = useState('');
    const [validationError, setValidationError] = useState('');
    const [showImpactedVerbatims, setShowImpactedVerbatims] = useState(false);
    const [mergedKeywords, setMergedKeywords] = useState([]);
    const [mergedExplanation, setMergedExplanation] = useState('');

    // Function to merge two sentiment states
    const mergeSentiments = (sentimentA, sentimentB) => {
        // Normalize sentiments (handle null/undefined)
        const normA = sentimentA || 'active';
        const normB = sentimentB || 'active';
        // If both are identical, keep that sentiment
        if (normA === normB) return normA;
        // Handle cases with 'mixed'
        if (normA === 'mixed' || normB === 'mixed') {
            // mixed + positive = positive
            if ((normA === 'mixed' && normB === 'positive') || (normA === 'positive' && normB === 'mixed')) {
                return 'positive';
            }
            // mixed + negative = negative
            if ((normA === 'mixed' && normB === 'negative') || (normA === 'negative' && normB === 'mixed')) {
                return 'negative';
            }
        }
        // positive + negative = mixed
        if ((normA === 'positive' && normB === 'negative') || (normA === 'negative' && normB === 'positive')) {
            return 'mixed';
        }
        // Default fallback
        return 'mixed';
    };

    // Reset selected labels when label type changes
    useEffect(() => {
        setSelectedLabels([]);
    }, [selectedLabelType, setSelectedLabels]);

    useEffect(() => {
        setNewLabelName('');
        setValidationError('');
        setMergedExplanation('');
        setMergedKeywords([]);
        setShowImpactedVerbatims(false);
    }, [selectedLabels]);

    useEffect(() => {
        // Merge keywords and explanations when selected labels change
        if (selectedLabels.length === 2 && selectedLabelType) {
            const [attrA, attrB] = selectedLabels;
            
            // Find labels
            const labelA = labels[selectedLabelType]?.find(l => l.name === attrA);
            const labelB = labels[selectedLabelType]?.find(l => l.name === attrB);
            // Merge keywords
            const keywordsA = labelA?.keywords || [];
            const keywordsB = labelB?.keywords || [];
            const uniqueKeywords = [...new Set([...keywordsA, ...keywordsB])];
            setMergedKeywords(uniqueKeywords.join(', '));
            // Merge explanations
            const explanationA = labelA?.explanation || '';
            const explanationB = labelB?.explanation || '';
            let combinedExplanation = '';
            
            if (explanationA && explanationB) {
                combinedExplanation = `${explanationA}\n${explanationB}`;
            } else if (explanationA) {
                combinedExplanation = explanationA;
            } else if (explanationB) {
                combinedExplanation = explanationB;
            }
            
            setMergedExplanation(combinedExplanation);
        } else {
            // Reset if not exactly 2 labels selected
            setMergedKeywords('');
            setMergedExplanation('');
        }
    }, [selectedLabels, selectedLabelType, labels]);

    useEffect(() => {
        // Validation of the new label name
        if (!newLabelName) {
            setValidationError('');
            return;
        }
        const nameExists = labels[selectedLabelType]?.some(
            label => label.name.toLowerCase() === newLabelName.toLowerCase() 
            && !selectedLabels.includes(label.name)
        );

        if (nameExists) {
            setValidationError('This label name already exists for this label type. Please choose a different name.');
        } else {
            setValidationError('');
        }
    }, [newLabelName, labels, selectedLabelType, selectedLabels]);

    // Calculate impacted verbatims
    const impactedVerbatims = useMemo(() => {
        if (selectedLabels.length !== 2) return [];

        const [attrA, attrB] = selectedLabels;
        const isSentimentEnabled = labelTypes[selectedLabelType]?.sentimentEnabled || false;

        return verbatims
            .map(verbatim => {
                const labelsForType = verbatim.labels?.[selectedLabelType] || [];
                
                const labelA = labelsForType.find(l => l.name === attrA);
                const labelB = labelsForType.find(l => l.name === attrB);

                // Si le verbatim contient au moins un des deux attributs
                if (labelA || labelB) {
                    // Calculer le sentiment résultant
                    let resultingSentiment = 'active';
                    
                    if (isSentimentEnabled) {
                        if (labelA && !labelB) {
                            resultingSentiment = labelA.state || 'active';
                        } else if (labelB && !labelA) {
                            resultingSentiment = labelB.state || 'active';
                        } else if (labelA && labelB) {
                            // Use the merge function to handle all sentiment combinations
                            resultingSentiment = mergeSentiments(labelA.state, labelB.state);
                        }
                    }

                    return {
                        id: verbatim.id,
                        verbatim: verbatim.verbatim,
                        hasA: !!labelA,
                        hasB: !!labelB,
                        sentimentA: labelA?.state || '-',
                        sentimentB: labelB?.state || '-',
                        resultingSentiment: resultingSentiment,
                        link: verbatim.link || ''
                    };
                }
                return null;
            })
            .filter(Boolean);
    }, [selectedLabels, selectedLabelType, verbatims, labelTypes]);

    const handleLabelClick = (labelName) => {
        const isAlreadySelected = selectedLabels.includes(labelName);
        // If already selected, we remove it
        if (isAlreadySelected) {
            setSelectedLabels(selectedLabels.filter(name => name !== labelName));
            return;
        }
        // If not selected and we already have 2 labels, show the alert
        if (selectedLabels.length >= 2) {
            alert("You can only select 2 attributes to merge.\nDeselect an attribute before selecting another.");
            return;
        }
        // Otherwise, we add
        setSelectedLabels([...selectedLabels, labelName]);
    };

    const renderVerbatimContent = (verbatim) => {
        if (!verbatim) return null;
    
        const content = verbatim;
        if (typeof content === 'string') {
          return (
            <>
              <p>{content}</p>
              {renderVerbatimTranslation(verbatim.translation)}
            </>
          );
        }
    
        if (Array.isArray(content)) {
          return (
            <div className="review-verbatim-content">
              {content.map((item, index) => {
                if (item.type === 'text' && item.content.trim() !== '') {
                  return (
                    <p key={index}>{item.content}</p>
                  );
                } 
                else if (item.type === 'image' && item.content) {
                  const imageUrl = base64ToImage(item.content);
                  return (
                    <div key={index} className="review-image-block">
                      <img 
                        src={imageUrl} 
                        alt="Verbatim" 
                        className="review-verbatim-image"
                        style={{ cursor: 'pointer' }}
                        onError={(e) => {
                          console.error("Erreur de chargement de l'image", e);
                          // Display a placeholder or error image if needed
                          e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                        }}
                      />
                    </div>
                  );
                }
                return null;
              })}
              {renderVerbatimTranslation(verbatim.translation)}
            </div>
          );
        }
    
        return null;
    };

    const handleMergeAttributes = () => {
        // Validation
        if (selectedLabels.length !== 2) {
            alert("Please select exactly 2 labels to merge.");
            return;
        }

        if (!newLabelName) {
            alert("Please enter a valid name for the new label.");
            return;
        }

        const [attrA, attrB] = selectedLabels;
        const isSentimentEnabled = labelTypes[selectedLabelType]?.sentimentEnabled || false;

        // Confirmation
        const confirmMessage = `
            ⚠️ IMPORTANT: Please save your project before proceeding!
            You are about to merge:
            • "${attrA}" + "${attrB}" → "${newLabelName}"
            • ${impactedVerbatims.length} verbatim(s) will be modified
            This action is IRREVERSIBLE.
            If you haven't saved your project recently, click "Cancel" and save it first.
            Continue with the merge?
            `.trim();

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            // Update verbatims
            const updatedVerbatims = verbatims.map(verbatim => {
                const labelsForType = verbatim.labels?.[selectedLabelType] || [];
                
                const labelA = labelsForType.find(l => l.name === attrA);
                const labelB = labelsForType.find(l => l.name === attrB);

                // if neither label is present, return the verbatim unchanged
                if (!labelA && !labelB) return verbatim;

                // Calculate the resulting sentiment
                let resultingSentiment = 'active';
                
                if (isSentimentEnabled) {
                    if (labelA && !labelB) {
                        resultingSentiment = labelA.state || 'active';
                    } else if (labelB && !labelA) {
                        resultingSentiment = labelB.state || 'active';
                    } else if (labelA && labelB) {
                        // Use the merge function to handle all sentiment combinations
                        resultingSentiment = mergeSentiments(labelA.state, labelB.state);
                    }
                }
                // Filter out the old labels
                const filteredLabels = labelsForType.filter(
                    l => l.name !== attrA && l.name !== attrB
                );
                // Add the new merged label
                const mergedLabel = {
                    name: newLabelName,
                    state: resultingSentiment,
                    type: selectedLabelType,
                };
                return {
                    ...verbatim,
                    labels: {
                        ...verbatim.labels,
                        [selectedLabelType]: [...filteredLabels, mergedLabel]
                    }
                };
            });

            // Update labels
            const updatedLabels = {
                ...labels,
                [selectedLabelType]: [
                    // Keep all labels except attrA and attrB
                    ...labels[selectedLabelType].filter(
                        label => label.name !== attrA && label.name !== attrB
                    ),
                    // Add the new label
                    {
                        name: newLabelName,
                        keywords: mergedKeywords.split(',').map(k => k.trim()).filter(k => k !== ''),
                        explanation: mergedExplanation,
                    }
                ]
            };

            // Update states
            setVerbatims(updatedVerbatims);
            setLabels(updatedLabels);
            alert(`✓ Merge successful!\n${impactedVerbatims.length} verbatim(s) have been updated.`);
            handleClose();
        } catch (error) {
            console.error("Error during label merge:", error);
            alert("An error occurred during the merge. Please try again.");
        }
    };

    const handleClose = () => {
        // Reset modal state on close
        setSelectedLabels([]);
        setNewLabelName('');
        setShowImpactedVerbatims(false);
        setSelectedLabelType(''); 
        setMergedExplanation('');
        setMergedKeywords([]);
        setValidationError('');  
        onClose();
    }

    return (
        <BaseModal
            show={isOpen}
            title="Merge Labels"
            onClose={handleClose}
            onConfirm={handleMergeAttributes}
            primaryActionLabel="Merge"
            secondaryActionLabel="Cancel"
        >   
            <div className="merge-attributes-modal">
                <div className="warning-message">
                    <div>
                        <i className="fa-solid fa-triangle-exclamation"></i>
                        <span style={{ margin: '5px 0 0 5px'}}>
                            The label merge operation is irreversible. It is strongly recommended to save your project before proceeding to avoid losing data in case of error.
                        </span>
                    </div>
                </div>
                <div className="section-container">
                    <div className="label-type-container">
                        <label className="label-type-label">
                            Label Type <span className="required">*</span>
                        </label>
                        <select
                            value={selectedLabelType}
                            onChange={(e) => setSelectedLabelType(e.target.value)}
                        >
                            <option value="">Select a label type</option>
                            {Object.keys(labelTypes).filter(type => !['Gender', 'Age', 'Sentiment', 'Sources'].includes(type)).map((labelType) => (
                                <option key={labelType} value={labelType}>
                                    {labelType}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="label-container">
                        <h3 className="label-type-label">Select Labels <span className="required">*</span></h3>
                        <div className="labels-list">
                            {labels[selectedLabelType]?.map((labelObj) => {
                                const { name } = labelObj;  // Extract label name and state
                                const stateClass = selectedLabels.includes(name) ? 'active' : 'inactive'; // Default to 'inactive' if state is not set

                                return (
                                    <button
                                        key={name}
                                        className={`label-button ${stateClass}`}  // Apply the correct class dynamically
                                        onClick={() => handleLabelClick(name)}
                                    >
                                        {name}
                                        &nbsp;
                                        {labelTypes[selectedLabelType]?.sentimentEnabled && (
                                            <i className="fa-solid fa-smile sentiment-icon" title="Sentiment Enabled"></i>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {selectedLabelType && selectedLabels.length === 2 && (
                        <>
                            <div className="merge-field-container">
                                <h3 className="label-type-label">Name the new label <span className="required">*</span></h3>
                                <input
                                    type="text"
                                    value={newLabelName}
                                    onChange={(e) => setNewLabelName(e.target.value)}
                                    placeholder="Ex: Value, Experience, Quality & Price..."
                                />
                                {validationError && (<small className="error-message">{validationError}</small>)}
                            </div>
                            {/* Section Keywords */}
                            <div className="merge-field-container">
                                <label className="label-type-label">
                                    Keywords (merged from both labels)
                                </label>
                                <textarea
                                    value={mergedKeywords}
                                    onChange={(e) => setMergedKeywords(e.target.value)}
                                    placeholder="Enter keywords separated by commas..."
                                />
                            </div>

                            {/* Section Explanation */}
                            <div className="merge-field-container">
                                <label className="label-type-label">
                                    Explanation (merged from both labels)
                                </label>
                                <textarea
                                    value={mergedExplanation}
                                    onChange={(e) => setMergedExplanation(e.target.value)}
                                    placeholder="Enter explanation..."
                                />
                            </div>
                        </>
                    )}
                </div>
                {/* Section to impact verbatims */}
                {selectedLabelType && selectedLabels.length === 2 && newLabelName.length > 0 && impactedVerbatims.length > 0 && (
                    <div>
                        <div className="impacted-header">
                            {impactedVerbatims.length > 0 ? 
                                <>
                                   <p>
                                        This label merge will impact {impactedVerbatims.length} verbatims in total: 
                                        {' '}{impactedVerbatims.filter(v => v.hasA && !v.hasB).length} with « {selectedLabels[0]} » only, 
                                        {' '}{impactedVerbatims.filter(v => !v.hasA && v.hasB).length} with « {selectedLabels[1]} » only, 
                                        and {impactedVerbatims.filter(v => v.hasA && v.hasB).length} with both labels; Click the button to view the affected verbatims.
                                        {labelTypes[selectedLabelType]?.sentimentEnabled && (
                                            <span className="sentiment-rules-block">
                                                <em>Sentiment merge rules applied :</em>
                                                <ul className="sentiment-rules-list">
                                                    <li><span>→</span> Positive + Positive = Positive</li>
                                                    <li><span>→</span> Negative + Negative = Negative</li>
                                                    <li><span>→</span> Mixed + Mixed = Mixed</li>
                                                    <li><span>→</span> Positive + Negative = Mixed</li>
                                                    <li><span>→</span> Positive + Mixed = Positive</li>
                                                    <li><span>→</span> Negative + Mixed = Negative</li>
                                                </ul>
                                            </span>
                                        )}
                                    </p>
                                    <div>
                                        <button 
                                            className="toggle-button"
                                            onClick={() => setShowImpactedVerbatims(!showImpactedVerbatims)}
                                        >
                                            {showImpactedVerbatims ? 'Hide' : 'Show'}
                                            <i className={`fa-solid ${showImpactedVerbatims ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                        </button>
                                    </div>
                                </> : 
                                <p>No verbatims will be impacted by this merge.</p>
                            }
                        </div>
    
                        {showImpactedVerbatims && (
                            <div className="verbatims-container">
                                <table className="verbatims-table">
                                    <thead>
                                        <tr>
                                            <th className="column-small">ID</th>
                                            <th className="column-very-large sticky-column">Verbatim</th>
                                            <th className="column-medium">{selectedLabelType} (Before Merge)</th>
                                            <th  className="column-medium">{selectedLabelType} (After Merge)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {impactedVerbatims.map((item) => (
                                            <tr key={item.id}>
                                                <td className="column-small">{item.id}</td>
                                                <td className="column-very-large sticky-column" data-verbatim-id={item.id} title={item.verbatimText}>
                                                    {renderVerbatimContent(item.verbatim)}
                                                </td>
                                                <td className="column-medium">
                                                    {item.hasA && item.hasB ? (
                                                        <>
                                                            <LabelChip
                                                                key={selectedLabels[0]}
                                                                type={selectedLabelType}
                                                                name={selectedLabels[0]}
                                                                state={item.sentimentA !== '-' ? item.sentimentA : 'active'}
                                                            />
                                                            <LabelChip
                                                                key={selectedLabels[1]}
                                                                type={selectedLabelType}
                                                                name={selectedLabels[1]}
                                                                state={item.sentimentB !== '-' ? item.sentimentB : 'active'}
                                                            />
                                                        </>
                                                    ) : item.hasA ? (
                                                        <LabelChip
                                                            key={selectedLabels[0]}
                                                            type={selectedLabelType}
                                                            name={selectedLabels[0]}
                                                            state={item.sentimentA !== '-' ? item.sentimentA : 'active'}
                                                        />
                                                    ) : (
                                                        <LabelChip
                                                            key={selectedLabels[1]}
                                                            type={selectedLabelType}
                                                            name={selectedLabels[1]}
                                                            state={item.sentimentB !== '-' ? item.sentimentB : 'active'}
                                                        />
                                                    )}
                                                </td>
                                                <td className="column-medium">
                                                    <LabelChip
                                                        key={newLabelName}
                                                        type={selectedLabelType}
                                                        name={newLabelName}
                                                        state={item.resultingSentiment}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </BaseModal>
    );
}

export default MergeLabelsModal;
